/**
 * RichTextEditor — a custom, reusable TinyMCE-style WYSIWYG editor.
 *
 * Features:
 *  - Formatting: bold, italic, underline, strike, headings, paragraph, blockquote, code
 *  - Lists (ordered / unordered), indent / outdent
 *  - Alignment (left / center / right / justify)
 *  - Text + background color, font family, font size
 *  - Link insert/edit, image insert (URL or upload as base64), table insert
 *  - Source Code modal — view & paste raw HTML
 *  - Fullscreen toggle, Undo/Redo, Clear formatting
 *  - Styled with plain CSS in styles.css
 *
 * No third-party editor dependency — built on contentEditable + execCommand.
 *
 * Usage:
 *   <RichTextEditor value={html} onChange={setHtml} placeholder="..." minHeight={400} />
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code as CodeIcon,
  List,
  ListOrdered,
  ListChecks,
  IndentDecrease,
  IndentIncrease,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link as LinkIcon,
  Unlink,
  Image as ImageIcon,
  Table as TableIcon,
  Palette,
  Highlighter,
  Undo2,
  Redo2,
  Eraser,
  Code2,
  Maximize2,
  Minimize2,
  X,
  Superscript,
  Subscript,
  Minus,
  Smile,
  Search,
  Printer,
  Plus,
  ChevronDown,
  FileUp,
  Paperclip,
  Loader2,
} from 'lucide-react';
import { cn } from './ui';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui';
import { Button } from './ui';
import { Input } from './ui';
import { Label } from './ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui';
import { Popover, PopoverContent, PopoverTrigger } from './ui';
import {
  ImageOptimizeError,
  optimizeImageFile,
  ACCEPTED_IMAGE_TYPES,
  extensionForMime,
} from './helpers';
import { tryUploadQuestionMedia } from './helpers';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import { ReusableSelect } from './ui';
import { isSafeUrl, normalizeLinkUrl } from './helpers';
import { renderMathElements } from './helpers';
import {
  ATTACHMENT_REMOVE_ATTR,
  buildFileAttachmentHtml,
  buildImageAttachmentHtml,
  buildMediaAttachmentHtml,
  decorateAttachmentRemoveButtons,
  ensureEditableTailAfterAttachments,
  formatAttachmentSize,
  lockAttachmentNodes,
  stripAttachmentRemoveButtons,
  upgradeLegacyFileAttachmentLinks,
} from './helpers';
const FONT_FAMILIES = [
  { label: 'Default', value: '' },
  { label: 'Sans Serif', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Serif', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Monospace', value: '"Courier New", Courier, monospace' },
  { label: 'Inter', value: 'Inter, system-ui, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
];
const FONT_SIZES = [
  { label: '10', value: '1' },
  { label: '12', value: '2' },
  { label: '14', value: '3' },
  { label: '16', value: '4' },
  { label: '18', value: '5' },
  { label: '24', value: '6' },
  { label: '32', value: '7' },
];
const FONT_SIZE_PX = {
  1: '10px',
  2: '12px',
  3: '14px',
  4: '16px',
  5: '18px',
  6: '24px',
  7: '32px',
};
const PRESET_COLORS = [
  '#000000',
  '#374151',
  '#6B7280',
  '#9CA3AF',
  '#D1D5DB',
  '#FFFFFF',
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#EAB308',
  '#84CC16',
  '#22C55E',
  '#10B981',
  '#14B8A6',
  '#06B6D4',
  '#0EA5E9',
  '#3B82F6',
  '#6366F1',
  '#8B5CF6',
  '#A855F7',
  '#D946EF',
  '#EC4899',
  '#F43F5E',
];
/* ── Toolbar primitives ─────────────────────────────────────────── */

const ToolBtn = ({ active, title, children, className, ...rest }) => (
  <Tooltip delayDuration={250}>
    <TooltipTrigger asChild>
      <button
        type='button'
        aria-label={title}
        onMouseDown={(e) => e.preventDefault() /* keep selection */}
        className={cn(
          'rte-tool',
          'rte-tool-colors',
          'rte-tool-disabled',
          active && 'rte-tool-active',
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    </TooltipTrigger>
    <TooltipContent side='bottom' className='rte-text-xs'>
      {title}
    </TooltipContent>
  </Tooltip>
);
const Divider = () => <div className='rte-divider' />;
/* ── Component ──────────────────────────────────────────────────── */
const RichTextEditor = forwardRef(
  (
    {
      value,
      onChange,
      placeholder = 'Start typing…',
      minHeight = 320,
      maxHeight,
      maxCharacters = 10000,
      className,
      disabled = false,
      sanitizeHtmlFn,
      allowAttachments = true,
      uploadFile,
      debounceMs = 300,
      notificationOptions = [],
    },
    ref,
  ) => {
    const sanitizeContent = useCallback(
      (html) => {
        // Always run our base sanitizer.
        const baseClean = sanitizeHtml(html);
        // Optional custom sanitizer can make it stricter,
        // but cannot bypass our base sanitizer.
        return sanitizeHtmlFn
          ? sanitizeHtml(sanitizeHtmlFn(baseClean))
          : baseClean;
      },
      [sanitizeHtmlFn],
    );
    const editorRef = useRef(null);
    const draggedImageRef = useRef(null);
    const historyRef = useRef({ items: [], index: -1 });
    const fullscreenHtmlRef = useRef(null);
    const editingLinkRef = useRef(null);
    const [linkError, setLinkError] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageRect, setImageRect] = useState(null);
    const [imageWidth, setImageWidth] = useState('');
    const [imagePosition, setImagePosition] = useState('inline');
    const [imageDropCaret, setImageDropCaret] = useState(null);
    const lastEmittedRef = useRef('');
    const lastValidHtmlRef = useRef('');
    const [messageDialog, setMessageDialog] = useState({
      open: false,
      title: '',
      message: '',
    });
    const showMessageDialog = (title, message) => {
      setMessageDialog({
        open: true,
        title,
        message,
      });
    };
    const getTextLength = (element) => {
      if (!element) return 0;
      return (element.innerText || element.textContent || '').replace(
        /\u200B/g,
        '',
      ).length;
    };
    const getHtmlTextLength = (html) => {
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return (temp.innerText || temp.textContent || '').replace(/\u200B/g, '')
        .length;
    };
    const moveCaretToEnd = () => {
      const editor = editorRef.current;
      if (!editor) return;
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    };
    const enforceCharacterLimit = () => {
      const editor = editorRef.current;
      if (!editor) return true;
      const length = getTextLength(editor);
      if (length <= maxCharacters) {
        lastValidHtmlRef.current = editor.innerHTML;
        return true;
      }
      editor.innerHTML = lastValidHtmlRef.current;
      moveCaretToEnd();
      return false;
    };
    const onChangeRef = useRef(onChange);
    const emitTimerRef = useRef(null);
    const [, force] = useState(0);
    onChangeRef.current = onChange;
    const [selectedFontSize, setSelectedFontSize] = useState('');
    const [selectedFontFamily, setSelectedFontFamily] = useState('');
    const imageInputRef = useRef(null);
    const [processing, setProcessing] = useState(false);
    const [insertError, setInsertError] = useState('');
    const refresh = useCallback(() => force((n) => n + 1), []);
    // Enable CSS-based styling so font-size/family persist and bold/italic/underline
    // can co-exist as inline styles on the same span.
    useEffect(() => {
      try {
        document.execCommand('styleWithCSS', false, 'true');
        document.execCommand('defaultParagraphSeparator', false, 'p');
      } catch {
        /* ignore */
      }
    }, []);
    // Modals
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkText, setLinkText] = useState('');
    const [tblOpen, setTblOpen] = useState(false);
    const [tblRows, setTblRows] = useState('');
    const [tblCols, setTblCols] = useState('');
    const [tblHeader, setTblHeader] = useState(true);
    // Reset table size inputs every time the dialog opens (no hardcoded defaults).
    useEffect(() => {
      if (tblOpen) {
        setTblRows('');
        setTblCols('');
        setTblHeader(true);
      }
    }, [tblOpen]);
    const [sourceOpen, setSourceOpen] = useState(false);
    const [sourceDraft, setSourceDraft] = useState('');
    const [fullscreen, setFullscreen] = useState(false);
    // Find & replace
    const [findOpen, setFindOpen] = useState(false);
    const [findQuery, setFindQuery] = useState('');
    const [replaceWith, setReplaceWith] = useState('');
    const [matchCase, setMatchCase] = useState(false);
    // Track whether caret is inside a table (to show table-edit menu)
    const [inTable, setInTable] = useState(false);
    // Keep saved selection so toolbar buttons / dialogs can restore caret
    const savedRange = useRef(null);
    const saveSelection = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const r = sel.getRangeAt(0);
      if (editorRef.current?.contains(r.commonAncestorContainer)) {
        savedRange.current = r.cloneRange();
      }
    };
    const restoreSelection = () => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();
      const selection = window.getSelection();
      if (!selection) return;
      const saved = savedRange.current;
      if (saved && editor.contains(saved.commonAncestorContainer)) {
        selection.removeAllRanges();
        selection.addRange(saved);
        return;
      }
      // Important: create an actual caret for the first insertion.
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
      savedRange.current = range.cloneRange();
    };
    // Sync external value -> editor. Entering/exiting fullscreen moves the
    // editor through a portal and creates a fresh contentEditable node. In that
    // case the value can still equal lastEmittedRef while the new DOM is empty,
    // so it must be hydrated again without resetting active typed content.
    useEffect(() => {
      const el = editorRef.current;
      if (!el) return;
      const incoming = fullscreenHtmlRef.current ?? value ?? '';
      fullscreenHtmlRef.current = null;
      const valueChanged = incoming !== lastEmittedRef.current;
      const freshEmptyEditor = el.innerHTML === '' && incoming !== '';
      if (valueChanged || freshEmptyEditor || historyRef.current.index < 0) {
        const clean = sanitizeContent(incoming);
        if (el.innerHTML !== clean) {
          el.innerHTML = clean;
        }
        lastEmittedRef.current = clean;
        upgradeLegacyFileAttachmentLinks(el);
        decorateAttachmentRemoveButtons(el);
        lockAttachmentNodes(el);
        el.querySelectorAll('img').forEach((img) => {
          img.draggable = true;
          img.style.cursor = 'move';
        });
        ensureEditableTailAfterAttachments(el);
        renderInlineMathElements(el);
        lastValidHtmlRef.current = el.innerHTML;
        const history = historyRef.current;
        if (history.items[history.index] !== clean) {
          history.items = [clean];
          history.index = 0;
        }
      }
    }, [value, sanitizeContent, fullscreen]);
    useEffect(
      () => () => {
        if (emitTimerRef.current) {
          clearTimeout(emitTimerRef.current);
          emitTimerRef.current = null;
        }
      },
      [],
    );
    const normalizeAttachmentTextColors = (root) => {
      const whiteColors = new Set([
        'white',
        '#fff',
        '#ffffff',
        'rgb(255, 255, 255)',
        'rgba(255, 255, 255, 1)',
      ]);
      root
        .querySelectorAll('[data-attachment], [data-attachment] *')
        .forEach((element) => {
          if (element.closest(`[${ATTACHMENT_REMOVE_ATTR}]`)) {
            return;
          }
          const inlineColor = element.style.color
            .trim()
            .toLowerCase()
            .replace(/\s+/g, ' ');
          if (whiteColors.has(inlineColor)) {
            element.style.removeProperty('color');
          }
          element.classList.remove('text-white');
        });
    };
    const refreshAttachmentDecorations = () => {
      const el = editorRef.current;
      if (!el) return;
      upgradeLegacyFileAttachmentLinks(el);
      decorateAttachmentRemoveButtons(el);
      lockAttachmentNodes(el);
      el.querySelectorAll('img').forEach((img) => {
        img.draggable = true;
        img.style.cursor = 'move';
      });
      ensureEditableTailAfterAttachments(el);
      normalizeAttachmentTextColors(el);
      renderInlineMathElements(el);
    };
    const getSerializableHtml = () => {
      if (!editorRef.current) return '';
      const scratch = document.createElement('div');
      scratch.innerHTML = editorRef.current.innerHTML;
      scratch
        .querySelectorAll('.math-equation[data-latex]')
        .forEach((equation) => {
          equation.innerHTML = '';
          equation.removeAttribute('data-rendered-latex');
          equation.setAttribute('contenteditable', 'false');
        });
      stripAttachmentRemoveButtons(scratch);
      // Skip DOMPurify on routine editor emits — attachments can embed large
      // base64 payloads and sanitizing on every keystroke causes severe lag.
      // External/pasted/source HTML is still sanitized when loaded into the editor.
      return scratch.innerHTML;
    };
    const emitNow = () => {
      if (emitTimerRef.current) {
        clearTimeout(emitTimerRef.current);
        emitTimerRef.current = null;
      }
      if (editorRef.current) {
        renderInlineMathElements(editorRef.current);
      }
      if (!enforceCharacterLimit())
        setInsertError(
          `Content cannot exceed ${maxCharacters.toLocaleString()} characters.`,
        );
      const cleanHtml = getSerializableHtml();
      const history = historyRef.current;
      if (history.items[history.index] !== cleanHtml) {
        history.items.splice(history.index + 1);
        history.items.push(cleanHtml);
        if (history.items.length > 100) history.items.shift();
        history.index = history.items.length - 1;
      }
      refresh();
      lastEmittedRef.current = cleanHtml;
      onChangeRef.current(cleanHtml);
      return cleanHtml;
    };
    const handleUploadedImage = useCallback(
      async (file) => {
        setInsertError('');
        setProcessing(true);
        try {
          const optimized = await optimizeImageFile(file);
          const filename = `question-image-${Date.now()}.${extensionForMime(optimized.mimeType)}`;
          const uploaded = await tryUploadQuestionMedia(
            optimized.blob,
            filename,
            uploadFile,
          );
          if ('error' in uploaded) {
            showMessageDialog(
              'Upload failed',
              `“${file.name}” could not be uploaded. ${uploaded.error}`,
            );
            return;
          }
          restoreSelection();
          document.execCommand(
            'insertHTML',
            false,
            `<p><img src="${escapeAttr(uploaded.url)}" alt="${escapeAttr(file.name)}" data-asset-id="${escapeAttr(uploaded.assetId)}" style="max-width:100%;height:auto;border-radius:8px;" /></p>`,
          );
          emit();
          saveSelection();
        } catch (error) {
          setInsertError(
            error instanceof ImageOptimizeError
              ? error.message
              : 'The image could not be processed.',
          );
        } finally {
          setProcessing(false);
        }
      },
      [uploadFile, disabled],
    );
    const scheduleEmit = () => {
      if (debounceMs === 0) {
        emitNow();
        return;
      }
      if (!enforceCharacterLimit()) {
        return;
      }
      if (emitTimerRef.current) {
        clearTimeout(emitTimerRef.current);
      }
      emitTimerRef.current = setTimeout(() => {
        emitTimerRef.current = null;
        emitNow();
      }, debounceMs);
    };
    const insertNotificationOption = (html) => {
      if (disabled || !editorRef.current) return;
      restoreSelection();
      editorRef.current.focus();
      document.execCommand('insertHTML', false, sanitizeContent(html));
      emitNow();
      saveSelection();
    };
    useImperativeHandle(ref, () => ({
      insertHtml: insertNotificationOption,
      flush: () => emitNow(),
      getHtml: () => getSerializableHtml(),
    }));
    const emit = emitNow;
    const updateImageRect = () => {
      if (!selectedImage?.isConnected) {
        setImageRect(null);
        return;
      }
      const rect = selectedImage.getBoundingClientRect();
      setImageRect({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      });
      setImageWidth(String(Math.round(rect.width)));
      setImagePosition(selectedImage.dataset.rteImagePosition || 'inline');
    };
    useEffect(() => {
      if (!selectedImage || disabled) {
        setImageRect(null);
        return;
      }
      updateImageRect();
      const update = () => updateImageRect();
      const observer = new ResizeObserver(update);
      observer.observe(selectedImage);
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      const close = (e) => {
        if (e.key === 'Escape') setSelectedImage(null);
      };
      window.addEventListener('keydown', close);
      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
        window.removeEventListener('keydown', close);
      };
    }, [selectedImage, disabled, fullscreen]);
    const resizeSelectedImage = (width) => {
      if (
        !selectedImage?.isConnected ||
        disabled ||
        !Number.isFinite(width) ||
        width < 24
      )
        return;
      selectedImage.style.width = `${Math.min(width, 4096)}px`;
      selectedImage.style.height = 'auto';
      selectedImage.style.maxWidth = '100%';
      selectedImage.removeAttribute('width');
      selectedImage.removeAttribute('height');
      emitNow();
      updateImageRect();
    };
    const positionSelectedImage = (position) => {
      if (!selectedImage?.isConnected || disabled) return;
      const img = selectedImage;
      img.dataset.rteImagePosition = position;
      img.style.float = 'none';
      img.style.verticalAlign = 'middle';
      img.style.display = position === 'inline' ? 'inline-block' : 'block';
      img.style.margin =
        position === 'inline'
          ? '0 4px'
          : position === 'center'
            ? '8px auto'
            : position === 'right'
              ? '8px 0 8px auto'
              : '8px auto 8px 0';
      if (position === 'wrapLeft' || position === 'wrapRight') {
        img.style.float = position === 'wrapLeft' ? 'left' : 'right';
        img.style.margin =
          position === 'wrapLeft' ? '4px 12px 8px 0' : '4px 0 8px 12px';
      }
      let attachment = img.closest('[data-attachment]');
      if (attachment) {
        const inline = position === 'inline';
        // Inline attachment wrappers must be spans, so they can live between words.
        if (
          (inline && attachment.tagName !== 'SPAN') ||
          (!inline && attachment.tagName !== 'FIGURE')
        ) {
          const replacement = document.createElement(
            inline ? 'span' : 'figure',
          );
          Array.from(attachment.attributes).forEach((attr) =>
            replacement.setAttribute(attr.name, attr.value),
          );
          while (attachment.firstChild)
            replacement.append(attachment.firstChild);
          attachment.replaceWith(replacement);
          attachment = replacement;
        }
        attachment.style.display = inline ? 'inline-block' : 'block';
        attachment.style.verticalAlign = 'middle';
        attachment.style.width =
          inline || position.startsWith('wrap') ? 'fit-content' : '100%';
        attachment.style.maxWidth = '100%';
        attachment.style.float =
          position === 'wrapLeft'
            ? 'left'
            : position === 'wrapRight'
              ? 'right'
              : 'none';
        attachment.style.margin = inline
          ? '0 4px'
          : position === 'wrapLeft'
            ? '4px 12px 8px 0'
            : position === 'wrapRight'
              ? '4px 0 8px 12px'
              : '8px 0';
        attachment.querySelectorAll('figcaption').forEach((caption) => {
          if (!inline) return;
          const span = document.createElement('span');
          span.innerHTML = caption.innerHTML;
          caption.replaceWith(span);
        });
        img.style.float = 'none';
      }
      // A floated image alone in its own paragraph cannot wrap adjacent paragraph text.
      const parent = (attachment || img).parentElement;
      if (
        position.startsWith('wrap') &&
        parent?.tagName === 'P' &&
        parent.parentElement === editorRef.current &&
        !parent.textContent.trim() &&
        parent.querySelectorAll('img').length === 1
      ) {
        parent.replaceWith(attachment || img);
      }
      refreshAttachmentDecorations();
      emitNow();
      updateImageRect();
    };
    const startImageResize = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      const startX = e.clientX,
        startWidth = selectedImage.getBoundingClientRect().width;
      const move = (event) => {
        const width = Math.max(
          24,
          Math.min(4096, startWidth + event.clientX - startX),
        );
        selectedImage.style.width = `${width}px`;
        selectedImage.style.height = 'auto';
        selectedImage.style.maxWidth = '100%';
        selectedImage.removeAttribute('width');
        selectedImage.removeAttribute('height');
        updateImageRect();
      };
      const stop = () => {
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', stop);
        handle.removeEventListener('pointercancel', stop);
        emitNow();
        updateImageRect();
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', stop);
      handle.addEventListener('pointercancel', stop);
    };
    const placeCaretInNode = (node, offset = 0) => {
      const sel = window.getSelection();
      if (!sel) return;
      const range = document.createRange();
      range.setStart(node, offset);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      savedRange.current = range.cloneRange();
    };
    /**
     * Apply font-family / font-size even when nothing is highlighted.
     * execCommand often no-ops on a collapsed caret; insert a styled ZWSP span
     * so the next typed characters inherit the chosen font.
     */
    const applyInlineFontStyle = (style) => {
      restoreSelection();
      editorRef.current?.focus();
      const sel = window.getSelection();
      if (!sel) return;
      if (sel.rangeCount === 0) {
        const fallback = document.createRange();
        const editor = editorRef.current;
        if (!editor) return;
        fallback.selectNodeContents(editor);
        fallback.collapse(false);
        sel.addRange(fallback);
      }
      const range = sel.getRangeAt(0);
      const collapsed = range.collapsed;
      if (!collapsed) {
        if (style.fontFamily) {
          document.execCommand('fontName', false, style.fontFamily);
        }
        if (style.fontSize) {
          // Native fontSize uses 1–7; map via CSS span for reliability.
          document.execCommand('styleWithCSS', false, 'false');
          document.execCommand('fontSize', false, '7');
          document.execCommand('styleWithCSS', false, 'true');
          const fonts = editorRef.current?.querySelectorAll('font[size="7"]');
          fonts?.forEach((fontEl) => {
            const span = document.createElement('span');
            span.style.fontSize = style.fontSize;
            while (fontEl.firstChild) span.appendChild(fontEl.firstChild);
            fontEl.parentNode?.replaceChild(span, fontEl);
          });
        }
        saveSelection();
        emit();
        refresh();
        return;
      }
      const span = document.createElement('span');
      if (style.fontFamily) span.style.fontFamily = style.fontFamily;
      if (style.fontSize) span.style.fontSize = style.fontSize;
      span.appendChild(document.createTextNode('\u200B'));
      range.insertNode(span);
      placeCaretInNode(span.firstChild, 1);
      emit();
      refresh();
    };
    const resetEditorColor = (type) => {
      if (disabled) return;
      restoreSelection();
      editorRef.current?.focus();
      if (type === 'text') {
        const defaultTextColor = editorRef.current
          ? window.getComputedStyle(editorRef.current).color
          : 'rgb(0, 0, 0)';
        document.execCommand('foreColor', false, defaultTextColor);
      } else {
        document.execCommand('hiliteColor', false, 'transparent');
      }
      saveSelection();
      emit();
      refresh();
    };
    const exec = (command, val) => {
      if (disabled) return;
      if (command === 'undo' || command === 'redo') {
        emitNow();
        const h = historyRef.current;
        const next = h.index + (command === 'undo' ? -1 : 1);
        if (next < 0 || next >= h.items.length) return;
        h.index = next;
        setSelectedImage(null);
        editorRef.current.innerHTML = h.items[next];
        lastValidHtmlRef.current = h.items[next];
        lastEmittedRef.current = h.items[next];
        refreshAttachmentDecorations();
        onChangeRef.current(h.items[next]);
        savedRange.current = null;
        restoreSelection();
        refresh();
        return;
      }
      if (command === 'fontName' && val) {
        applyInlineFontStyle({ fontFamily: val });
        return;
      }
      if (command === 'fontSize' && val) {
        applyInlineFontStyle({ fontSize: FONT_SIZE_PX[val] || `${val}px` });
        return;
      }
      restoreSelection();
      editorRef.current?.focus();
      document.execCommand(command, false, val);
      saveSelection();
      emit();
      refresh();
    };
    const isActive = (command) => {
      try {
        return document.queryCommandState(command);
      } catch {
        return false;
      }
    };
    const currentBlock = () => {
      try {
        return (document.queryCommandValue('formatBlock') || '').toLowerCase();
      } catch {
        return '';
      }
    };
    const setBlock = (tag) => exec('formatBlock', `<${tag}>`);
    /* ── Link ── */
    const openLink = () => {
      setLinkError('');
      saveSelection();
      const sel = window.getSelection();
      setLinkText(sel?.toString() || '');
      setLinkUrl('');
      // If cursor is inside an existing <a>, prefill
      const node = sel?.anchorNode;
      let a = null;
      let cur = node || null;
      while (cur && cur !== editorRef.current) {
        if (cur.nodeType === 1 && cur.tagName === 'A') {
          a = cur;
          break;
        }
        cur = cur.parentNode;
      }
      editingLinkRef.current = a;
      if (a) {
        setLinkUrl(a.getAttribute('href') || '');
        setLinkText(a.textContent || '');
      }
      setLinkOpen(true);
    };
    const applyLink = () => {
      if (disabled) return;
      const url = normalizeLinkUrl(linkUrl);
      if (!url) {
        setLinkError(
          'Enter a valid website URL, email link, telephone link, or relative URL.',
        );
        return;
      }
      const editor = editorRef.current;
      if (!editor) return;
      if (editingLinkRef.current && editor.contains(editingLinkRef.current)) {
        const anchor = editingLinkRef.current;
        anchor.href = url;
        anchor.textContent = linkText.trim() || url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
      } else {
        // Insert through the saved Range: modal focus trapping cannot redirect insertion.
        const range =
          savedRange.current?.cloneRange() || document.createRange();
        if (!editor.contains(range.commonAncestorContainer)) {
          range.selectNodeContents(editor);
          range.collapse(false);
        }
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.textContent = linkText.trim() || range.toString() || url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        range.deleteContents();
        range.insertNode(anchor);
        range.setStartAfter(anchor);
        range.collapse(true);
        savedRange.current = range.cloneRange();
      }
      emitNow();
      setLinkError('');
      setLinkOpen(false);
      requestAnimationFrame(() => restoreSelection());
    };
    const removeLink = () => {
      if (disabled) return;
      restoreSelection();
      editorRef.current?.focus();
      const sel = window.getSelection();
      if (sel) {
        // If caret is collapsed inside an <a>, select the entire link first
        // so execCommand('unlink') actually has something to unwrap.
        let cur = sel.anchorNode;
        let a = null;
        while (cur && cur !== editorRef.current) {
          if (cur.nodeType === 1 && cur.tagName === 'A') {
            a = cur;
            break;
          }
          cur = cur.parentNode;
        }
        if (a) {
          const r = document.createRange();
          r.selectNodeContents(a);
          sel.removeAllRanges();
          sel.addRange(r);
        }
      }
      document.execCommand('unlink');
      saveSelection();
      emit();
      refresh();
    };
    /* ── Table ── */
    const insertTable = () => {
      if (disabled) return;
      const rowsNum = parseInt(tblRows, 10);
      const colsNum = parseInt(tblCols, 10);
      if (!rowsNum || !colsNum || rowsNum < 1 || colsNum < 1) return;
      const rows = Math.max(1, Math.min(20, rowsNum));
      const cols = Math.max(1, Math.min(10, colsNum));
      let html =
        '<table border="1" style="border-collapse:collapse;width:100%;margin:8px 0;">';
      if (tblHeader) {
        html += '<thead><tr>';
        for (let c = 0; c < cols; c++) {
          html += `<th style="border:1px solid #d1d5db;padding:8px;background:#f3f4f6;text-align:left;"><p style="margin:0;">Header ${c + 1}</p></th>`;
        }
        html += '</tr></thead>';
      }
      html += '<tbody>';
      const bodyRows = tblHeader ? rows - 1 : rows;
      for (let r = 0; r < Math.max(0, bodyRows); r++) {
        html += '<tr>';
        for (let c = 0; c < cols; c++) {
          html += `<td style="border:1px solid #d1d5db;padding:8px;"><p style="margin:0;">&nbsp;</p></td>`;
        }
        html += '</tr>';
      }
      html += '</tbody></table><p><br></p>';
      restoreSelection();
      document.execCommand('insertHTML', false, html);
      emit();
      setTblOpen(false);
    };
    /* ── Source code ── */
    // Strip leftover empty inline tags (e.g. `<span style="…"></span>` left
    // behind after the user deletes all text) so the source view reflects
    // only the meaningful HTML.
    const getCleanedHtml = () => {
      const root = editorRef.current;
      if (!root) return '';
      const clone = root.cloneNode(true);
      stripAttachmentRemoveButtons(clone);
      const KEEP = new Set([
        'IMG',
        'BR',
        'HR',
        'TABLE',
        'TBODY',
        'THEAD',
        'TFOOT',
        'TR',
        'TD',
        'TH',
        'INPUT',
        'IFRAME',
        'VIDEO',
        'AUDIO',
        'SOURCE',
      ]);
      const isEmpty = (el) => {
        if (
          el.matches('[data-attachment], [data-latex]') ||
          KEEP.has(el.tagName)
        )
          return false;
        if (el.querySelector('img,br,hr,input,table,iframe,video,audio'))
          return false;
        const text = (el.textContent || '').replace(/[\u200B\u00A0\s]+/g, '');
        return text.length === 0;
      };
      const sweep = (parent) => {
        Array.from(parent.children).forEach((c) => sweep(c));
        Array.from(parent.children).forEach((c) => {
          if (isEmpty(c)) c.remove();
        });
      };
      sweep(clone);
      return clone.innerHTML;
    };
    const openSource = () => {
      setSourceDraft(getCleanedHtml());
      setSourceOpen(true);
    };
    const applySource = () => {
      setSelectedImage(null);
      if (disabled) return;
      const cleanHtml = sanitizeContent(sourceDraft);
      const textLength = getHtmlTextLength(cleanHtml);
      if (textLength > maxCharacters) {
        setInsertError(
          `Content cannot exceed ${maxCharacters.toLocaleString()} characters.`,
        );
        return;
      }
      if (editorRef.current) {
        editorRef.current.innerHTML = cleanHtml;
        renderInlineMathElements(editorRef.current);
        lastValidHtmlRef.current = editorRef.current.innerHTML;
        lastEmittedRef.current = cleanHtml;
        refreshAttachmentDecorations();
        emitNow();
      }
      setInsertError('');
      setSourceOpen(false);
      refresh();
    };
    const insertChecklist = () => {
      if (disabled) return;
      restoreSelection();
      editorRef.current?.focus();
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const selectedText = selection.toString().trim();
      const sentences = selectedText
        ? selectedText
            .split(/\r?\n+|(?<=[.!?])\s+(?=[A-Z0-9])/)
            .map((sentence) => sentence.trim())
            .filter(Boolean)
        : [''];
      const items = sentences
        .map(
          (sentence) => `
        <li
          style="
            display:flex;
            align-items:flex-start;
            gap:8px;
            margin:6px 0;
            list-style:none;
          "
        >
          <span
            contenteditable="false"
            data-check-ui="true" data-checked="true" role="checkbox" aria-checked="true" tabindex="0"
            style="
              width:18px;
              height:18px;
              min-width:18px;
              display:inline-flex;
              align-items:center;
              justify-content:center;
              margin-top:2px;
              border-radius:50%;
              color:#ffffff;
              font-size:12px;
              font-weight:700;
              line-height:1;
              user-select:none;
              cursor:pointer; background:#7c3aed; border:1px solid #7c3aed;
            "
          >✓</span>

          <span style="flex:1;min-width:0;">
            ${escapeHtml(sentence) || '\u200B'}
          </span>
        </li>
      `,
        )
        .join('');
      const html = `
    <ul
      data-checklist="true"
      style="
        list-style:none;
        padding-left:0;
        margin:8px 0;
      "
    >
      ${items}
    </ul>
    <p><br /></p>
  `;
      document.execCommand('insertHTML', false, sanitizeContent(html));
      saveSelection();
      emit();
      refresh();
    };
    const closestTableCell = () => {
      const sel = window.getSelection();
      let node = sel?.anchorNode || null;
      while (node && node !== editorRef.current) {
        if (node.nodeType === 1) {
          const el = node;
          if (el.tagName === 'TD' || el.tagName === 'TH') {
            const cell = el;
            const row = cell.parentElement;
            const table = row?.closest('table');
            if (table) return { cell, row, table };
          }
        }
        node = node.parentNode;
      }
      return null;
    };
    const renderInlineMathElements = (root) => {
      root
        .querySelectorAll(
          '.math-equation[data-latex]:not([data-rendered-latex])',
        )
        .forEach((equation) => {
          let inlineEquation = equation;
          // A div always creates a separate line, so replace it with a span.
          if (equation.tagName !== 'SPAN') {
            const span = document.createElement('span');
            Array.from(equation.attributes).forEach((attribute) => {
              span.setAttribute(attribute.name, attribute.value);
            });
            equation.replaceWith(span);
            inlineEquation = span;
          }
          inlineEquation.setAttribute('data-display-mode', 'false');
          inlineEquation.setAttribute('contenteditable', 'false');
          inlineEquation.style.display = 'inline-block';
          inlineEquation.style.verticalAlign = 'middle';
          // Force KaTeX to rerender using inline mode.
          inlineEquation.innerHTML = '';
          inlineEquation.removeAttribute('data-rendered-latex');
        });
      renderMathElements(root);
    };
    const cellTemplate =
      '<td style="border:1px solid #d1d5db;padding:8px;"><p style="margin:0;">&nbsp;</p></td>';
    const headerCellTemplate =
      '<th style="border:1px solid #d1d5db;padding:8px;background:#f3f4f6;text-align:left;"><p style="margin:0;">Header</p></th>';
    const runTableAction = (action) => {
      if (disabled) return;
      restoreSelection();
      const ctx = closestTableCell();
      if (!ctx) return;
      const { cell, row, table } = ctx;
      const colIndex = Array.from(row.cells).indexOf(cell);
      const cols = row.cells.length;
      if (action === 'addRowAbove' || action === 'addRowBelow') {
        const newRow = document.createElement('tr');
        newRow.innerHTML = Array(cols).fill(cellTemplate).join('');
        if (action === 'addRowAbove')
          row.parentElement?.insertBefore(newRow, row);
        else row.parentElement?.insertBefore(newRow, row.nextSibling);
      }
      if (action === 'deleteRow') {
        if (table.rows.length > 1) row.remove();
      }
      if (action === 'addColLeft' || action === 'addColRight') {
        Array.from(table.rows).forEach((r) => {
          const targetCell = r.cells[colIndex];
          const isHeaderRow =
            r.parentElement?.tagName === 'THEAD' ||
            r.cells[0]?.tagName === 'TH';
          const tpl = isHeaderRow ? headerCellTemplate : cellTemplate;
          const newCell = document.createElement('div');
          newCell.innerHTML = tpl;
          const node = newCell.firstChild;
          if (action === 'addColLeft') r.insertBefore(node, targetCell);
          else r.insertBefore(node, targetCell.nextSibling);
        });
      }
      if (action === 'deleteCol') {
        if (cols > 1) {
          Array.from(table.rows).forEach((r) => r.cells[colIndex]?.remove());
        }
      }
      if (action === 'deleteTable') {
        table.remove();
      }
      if (action === 'toggleHeaderRow') {
        const first = table.rows[0];
        if (first) {
          const isHeader = first.cells[0]?.tagName === 'TH';
          Array.from(first.cells).forEach((c) => {
            const replacement = document.createElement(isHeader ? 'td' : 'th');
            replacement.innerHTML = c.innerHTML;
            replacement.setAttribute(
              'style',
              isHeader
                ? 'border:1px solid #d1d5db;padding:8px;'
                : 'border:1px solid #d1d5db;padding:8px;background:#f3f4f6;text-align:left;',
            );
            c.replaceWith(replacement);
          });
        }
      }
    };
    const matchesRef = useRef([]);
    const matchIndexRef = useRef(-1);
    const [findStatus, setFindStatus] = useState('');
    const collectMatches = useCallback(() => {
      matchesRef.current = [];
      if (!editorRef.current || !findQuery) {
        setFindStatus('');
        return;
      }
      const flags = matchCase ? 'g' : 'gi';
      const escaped = findQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(escaped, flags);
      const walker = document.createTreeWalker(
        editorRef.current,
        NodeFilter.SHOW_TEXT,
      );
      let n;
      while ((n = walker.nextNode())) {
        const t = n;
        if (t.parentElement?.closest('[contenteditable="false"]')) continue;
        let m;
        re.lastIndex = 0;
        while ((m = re.exec(t.data))) {
          matchesRef.current.push({
            node: t,
            start: m.index,
            end: m.index + m[0].length,
          });
          if (m[0].length === 0) re.lastIndex++;
        }
      }
      setFindStatus(
        matchesRef.current.length
          ? `${matchesRef.current.length} match${matchesRef.current.length === 1 ? '' : 'es'}`
          : 'No matches',
      );
    }, [findQuery, matchCase]);
    const selectMatch = (i) => {
      const m = matchesRef.current[i];
      if (!m || !editorRef.current) return;
      const range = document.createRange();
      range.setStart(m.node, m.start);
      range.setEnd(m.node, m.end);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      savedRange.current = range.cloneRange();
      m.node.parentElement?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      setFindStatus(`${i + 1} of ${matchesRef.current.length}`);
    };
    const findNext = () => {
      collectMatches();
      if (matchesRef.current.length === 0) return;
      matchIndexRef.current =
        (matchIndexRef.current + 1) % matchesRef.current.length;
      selectMatch(matchIndexRef.current);
    };
    const replaceCurrent = () => {
      if (disabled) return;
      if (matchesRef.current.length === 0 || matchIndexRef.current < 0) {
        findNext();
        return;
      }
      const m = matchesRef.current[matchIndexRef.current];
      if (!m) return;
      const before = m.node.data.slice(0, m.start);
      const after = m.node.data.slice(m.end);
      m.node.data = before + replaceWith + after;
      emit();
      // Re-collect since text changed; advance to the next occurrence.
      collectMatches();
      if (matchesRef.current.length > 0) {
        matchIndexRef.current =
          matchIndexRef.current % matchesRef.current.length;
        selectMatch(matchIndexRef.current);
      } else {
        matchIndexRef.current = -1;
      }
    };
    const replaceAllMatches = () => {
      if (disabled) return;
      collectMatches();
      if (matchesRef.current.length === 0) return;
      const total = matchesRef.current.length;
      const byNode = new Map();
      matchesRef.current.forEach((m) => {
        if (!byNode.has(m.node)) byNode.set(m.node, []);
        byNode.get(m.node).push(m);
      });
      byNode.forEach((arr, node) => {
        arr.sort((a, b) => b.start - a.start);
        let s = node.data;
        arr.forEach((r) => {
          s = s.slice(0, r.start) + replaceWith + s.slice(r.end);
        });
        node.data = s;
      });
      emit();
      matchesRef.current = [];
      matchIndexRef.current = -1;
      setFindStatus(`Replaced ${total}`);
    };
    // Reset match index whenever the query/case changes
    useEffect(() => {
      matchIndexRef.current = -1;
      if (findOpen) collectMatches();
    }, [findQuery, matchCase, findOpen, collectMatches]);
    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      let animationFrame = null;
      const observer = new MutationObserver(() => {
        const hasUnrenderedEquation = editor.querySelector(
          '.math-equation[data-latex]:not([data-rendered-latex])',
        );
        if (!hasUnrenderedEquation) return;
        if (animationFrame !== null) {
          cancelAnimationFrame(animationFrame);
        }
        animationFrame = requestAnimationFrame(() => {
          renderInlineMathElements(editor);
          animationFrame = null;
        });
      });
      observer.observe(editor, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-latex'],
      });
      return () => {
        observer.disconnect();
        if (animationFrame !== null) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }, [fullscreen]);
    /* ── Print ── */
    const printContent = () => {
      const html = sanitizeHtml(editorRef.current?.innerHTML || '');
      const win = window.open('', '_blank', 'width=900,height=700');
      if (!win) return;
      win.opener = null;
      win.document.write(`<!doctype html><html><head><title>Print</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;line-height:1.6;color:#111;}
      img{max-width:100%;height:auto;}table{border-collapse:collapse;}td,th{border:1px solid #d1d5db;padding:8px;}</style>
      </head><body>${html}</body></html>`);
      win.document.close();
      setTimeout(() => {
        win.focus();
        win.print();
      }, 300);
    };
    /* ── Auto-link detection ── */
    // When the user types a space/enter after a URL, wrap that URL in <a>.
    const URL_RE =
      /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[\w.+-]+@[\w-]+\.[\w.-]+)$/i;
    const autoLinkBeforeCaret = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) return;
      const node = range.startContainer;
      if (node.nodeType !== Node.TEXT_NODE) return;
      if (!editorRef.current?.contains(node)) return;
      // Don't re-link if already inside an <a>
      let cur = node.parentNode;
      while (cur && cur !== editorRef.current) {
        if (cur.nodeType === 1 && cur.tagName === 'A') return;
        cur = cur.parentNode;
      }
      const textNode = node;
      const offset = range.startOffset;
      // The trigger char (space/enter) is already inserted — examine text BEFORE it.
      const before = textNode.data.slice(0, Math.max(0, offset - 1));
      const match = before.match(URL_RE);
      if (!match) return;
      const url = match[0];
      const start = before.length - url.length;
      const urlRange = document.createRange();
      urlRange.setStart(textNode, start);
      urlRange.setEnd(textNode, start + url.length);
      const href =
        url.includes('@') && !url.startsWith('http')
          ? `mailto:${url}`
          : url.startsWith('http')
            ? url
            : `https://${url}`;
      const a = document.createElement('a');
      a.href = href;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = url;
      urlRange.deleteContents();
      urlRange.insertNode(a);
      const after = document.createRange();
      after.setStartAfter(a);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
      emit();
    };
    const changeIndent = (direction) => {
      if (disabled) return;
      restoreSelection();
      editorRef.current?.focus();
      const selection = window.getSelection();
      if (!selection?.rangeCount || !editorRef.current) return;
      let node = selection.anchorNode;
      if (node?.nodeType === Node.TEXT_NODE) {
        node = node.parentElement;
      }
      const element = node;
      const listItem = element?.closest('li');
      // Preserve normal nested-list behavior.
      if (listItem && editorRef.current.contains(listItem)) {
        document.execCommand(
          direction === 'increase' ? 'indent' : 'outdent',
          false,
        );
        saveSelection();
        emit();
        refresh();
        return;
      }
      const block = element?.closest(
        'p, div, h1, h2, h3, h4, h5, h6, blockquote, pre',
      );
      if (!block || !editorRef.current.contains(block)) return;
      const currentIndent =
        Number.parseInt(block.style.marginLeft || '0', 10) || 0;
      const nextIndent =
        direction === 'increase'
          ? Math.min(currentIndent + 32, 320)
          : Math.max(currentIndent - 32, 0);
      if (nextIndent === 0) {
        block.style.removeProperty('margin-left');
      } else {
        block.style.marginLeft = `${nextIndent}px`;
      }
      saveSelection();
      emit();
      refresh();
    };
    const insertHtmlAtCaret = (html) => {
      const editor = editorRef.current;
      if (!editor) return;
      restoreSelection();
      editor.focus();
      const tailId = `rte-tail-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;
      if (disabled) return;
      const safeHtml = sanitizeContent(html);
      /*
       * Use a real paragraph, not a span.
       * This gives the user a clean editable line after the attachment.
       */
      const editableTail = `
    <p
      data-rte-tail-id="${tailId}"
      style="
        margin: 8px 0 0;
        color: hsl(var(--foreground));
        background: transparent;
        font-weight: 400;
        font-style: normal;
        text-decoration: none;
      "
    ><br></p>
  `;
      document.execCommand('insertHTML', false, `${safeHtml}${editableTail}`);
      let tail = editor.querySelector(`[data-rte-tail-id="${tailId}"]`);
      if (!tail) {
        tail = document.createElement('p');
        tail.dataset.rteTailId = tailId;
        tail.innerHTML = '<br>';
        tail.style.margin = '8px 0 0';
        tail.style.color = 'hsl(var(--foreground))';
        tail.style.backgroundColor = 'transparent';
        editor.appendChild(tail);
      }
      /*
       * Chrome may place the paragraph inside the attachment.
       * Explicitly move it outside.
       */
      const parentAttachment = tail.closest('[data-attachment]');
      if (parentAttachment) {
        parentAttachment.insertAdjacentElement('afterend', tail);
      }
      refreshAttachmentDecorations();
      renderInlineMathElements(editor);
      // Re-query because the DOM may have been modified by the decoration helpers.
      const finalTail =
        editor.querySelector(`[data-rte-tail-id="${tailId}"]`) ?? tail;
      finalTail.style.setProperty(
        'color',
        'hsl(var(--foreground))',
        'important',
      );
      finalTail.style.setProperty(
        'background-color',
        'transparent',
        'important',
      );
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(finalTail);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
      savedRange.current = range.cloneRange();
      emit();
      refresh();
    };
    const fileToDataUrl = (file) =>
      new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || ''));
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
      });
    const fileToArrayBuffer = (file) =>
      new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(r.error);
        r.readAsArrayBuffer(file);
      });
    const fileToText = (file) =>
      new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || ''));
        r.onerror = () => reject(r.error);
        r.readAsText(file);
      });
    const importDocumentFile = async (file) => {
      const name = file.name.toLowerCase();
      try {
        if (file.type.startsWith('image/')) {
          const dataUrl = await fileToDataUrl(file);
          insertHtmlAtCaret(
            `<p><img src="${escapeAttr(dataUrl)}" alt="${escapeAttr(file.name)}" style="max-width:100%;height:auto;" /></p>`,
          );
          return;
        }
        if (name.endsWith('.docx')) {
          const mammoth = await import('mammoth');
          const buf = await fileToArrayBuffer(file);
          const result = await (mammoth.default ?? mammoth).convertToHtml({
            arrayBuffer: buf,
          });
          insertHtmlAtCaret(cleanWordHtml(result.value || ''));
          return;
        }
        if (name.endsWith('.html') || name.endsWith('.htm')) {
          const txt = await fileToText(file);
          // Extract <body> if present
          const m = txt.match(/<body[^>]*>([\s\S]*)<\/body>/i);
          insertHtmlAtCaret(cleanWordHtml(m ? m[1] : txt));
          return;
        }
        if (name.endsWith('.md') || name.endsWith('.markdown')) {
          const txt = await fileToText(file);
          const { marked } = await import('marked');
          insertHtmlAtCaret(String(marked.parse(txt)));
          return;
        }
        if (name.endsWith('.txt')) {
          const txt = await fileToText(file);
          const html = txt
            .split(/\r?\n/)
            .map((line) => `<p>${escapeHtml(line) || '<br>'}</p>`)
            .join('');
          insertHtmlAtCaret(html);
          return;
        }
        if (name.endsWith('.pdf')) {
          showMessageDialog(
            'PDF import not supported',
            'PDF import is not supported here. Open the PDF, copy the text, then paste it into the editor.',
          );
          return;
        }
        showMessageDialog(
          'Unsupported file type',
          `"${file.name}" is not a supported document type.`,
        );
      } catch (err) {
        console.error('[RichTextEditor] import failed', err);
        showMessageDialog(
          'Import failed',
          `Could not import "${file.name}". ${err.message || 'Please try another file.'}`,
        );
      }
    };
    const importInputRef = useRef(null);
    const openImport = () => {
      if (disabled) return;
      saveSelection();
      importInputRef.current?.click();
    };
    const onImportChange = async (e) => {
      const f = e.target.files?.[0];
      if (f) await importDocumentFile(f);
      if (importInputRef.current) importInputRef.current.value = '';
    };
    const addAssetIdToAttachmentHtml = (html, assetId) => {
      const container = document.createElement('div');
      container.innerHTML = html;
      const attachment = container.querySelector('[data-attachment]');
      if (attachment) {
        attachment.setAttribute('data-asset-id', assetId);
      }
      return container.innerHTML;
    };
    // ── Attach file (PDF rendered inline, images/video/audio embedded, others = chip) ──
    const attachInputRef = useRef(null);
    const [attaching, setAttaching] = useState(false);
    const openAttach = () => {
      if (disabled) return;
      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();
      const selection = window.getSelection();
      // Ensure the first upload has a valid selection.
      if (
        !selection ||
        selection.rangeCount === 0 ||
        !editor.contains(selection.getRangeAt(0).commonAncestorContainer)
      ) {
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
        savedRange.current = range.cloneRange();
      } else {
        saveSelection();
      }
      attachInputRef.current?.click();
    };
    const fmtSize = formatAttachmentSize;
    const handleAttachFile = async (file) => {
      const MAX = 10 * 1024 * 1024;
      if (file.size > MAX) {
        showMessageDialog(
          'File too large',
          `"${file.name}" exceeds the maximum allowed size of ${fmtSize(MAX)}.`,
        );
        return;
      }
      setAttaching(true);
      setInsertError('');
      try {
        // Upload the actual file to the backend.
        const uploaded = await tryUploadQuestionMedia(
          file,
          file.name,
          uploadFile,
        );
        if ('error' in uploaded) {
          setInsertError(
            `“${file.name}” could not be uploaded. ${uploaded.error}`,
          );
          return;
        }
        const { url, assetId } = uploaded;
        let attachmentHtml = '';
        if (file.type === 'application/pdf') {
          /*
           * Do NOT convert PDF pages to canvas/base64.
           * Store the backend URL just like any other file.
           */
          attachmentHtml = buildFileAttachmentHtml(file.name, file.size, url);
        } else if (file.type.startsWith('image/')) {
          attachmentHtml = buildImageAttachmentHtml(file.name, url);
        } else if (file.type.startsWith('video/')) {
          attachmentHtml = buildMediaAttachmentHtml('video', file.name, url);
        } else if (file.type.startsWith('audio/')) {
          attachmentHtml = buildMediaAttachmentHtml('audio', file.name, url);
        } else {
          attachmentHtml = buildFileAttachmentHtml(file.name, file.size, url);
        }
        attachmentHtml = addAssetIdToAttachmentHtml(attachmentHtml, assetId);
        insertHtmlAtCaret(attachmentHtml);
      } catch (error) {
        console.error('[RichTextEditor] attachment upload failed', error);
        showMessageDialog(
          'Upload failed',
          error instanceof Error
            ? error.message
            : `Could not attach "${file.name}".`,
        );
      } finally {
        setAttaching(false);
        if (attachInputRef.current) {
          attachInputRef.current.value = '';
        }
      }
    };
    const onAttachChange = async (e) => {
      const f = e.target.files?.[0];
      if (f) await handleAttachFile(f);
    };
    const onPaste = async (e) => {
      const cd = e.clipboardData;
      // Image on clipboard (screenshot, copied image)
      const imgFile = Array.from(cd.files || []).find((f) =>
        f.type.startsWith('image/'),
      );
      if (imgFile) {
        e.preventDefault();
        const dataUrl = await fileToDataUrl(imgFile);
        insertHtmlAtCaret(
          `<img src="${escapeAttr(dataUrl)}" alt="" style="max-width:100%;height:auto;" />`,
        );
        return;
      }
      // .docx / other supported file dropped via copy
      const docFile = Array.from(cd.files || []).find((f) =>
        /\.(docx|html?|md|markdown|txt)$/i.test(f.name),
      );
      if (docFile) {
        e.preventDefault();
        await importDocumentFile(docFile);
        return;
      }
      const html = cd.getData('text/html');
      const text = cd.getData('text/plain');
      if (html) {
        e.preventDefault();
        const cleanedHtml = sanitizeContent(cleanWordHtml(html));
        document.execCommand('insertHTML', false, cleanedHtml);
        if (!enforceCharacterLimit()) {
          return;
        }
        emit();
      } else if (text) {
        e.preventDefault();
        const editor = editorRef.current;
        if (!editor) return;
        const currentLength = getTextLength(editor);
        const selection = window.getSelection();
        const selectedLength =
          selection && editor.contains(selection.anchorNode)
            ? selection.toString().length
            : 0;
        const available = maxCharacters - (currentLength - selectedLength);
        if (available <= 0) return;
        const safeText = text.slice(0, available);
        document.execCommand('insertText', false, safeText);
        emit();
      }
    };
    const imageDropRange = (x, y) => {
      const editor = editorRef.current;
      let range = document.caretRangeFromPoint?.(x, y);
      if (!range && document.caretPositionFromPoint) {
        const position = document.caretPositionFromPoint(x, y);
        if (position) {
          range = document.createRange();
          range.setStart(position.offsetNode, position.offset);
          range.collapse(true);
        }
      }
      if (!range || !editor.contains(range.commonAncestorContainer)) {
        range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
      }
      return range;
    };
    const onImageDragStart = (e) => {
      const image = e.target.closest?.('img');
      if (!image || !editorRef.current.contains(image)) return;
      if (disabled) {
        e.preventDefault();
        return;
      }
      emitNow();
      draggedImageRef.current = image;
      setSelectedImage(null);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/x-rte-image', 'move');
      e.dataTransfer.setData('text/plain', image.alt || 'Image');
    };
    const onEditorDrop = async (e) => {
      const image = draggedImageRef.current;
      if (image?.isConnected && editorRef.current.contains(image)) {
        e.preventDefault();
        e.stopPropagation();
        setImageDropCaret(null);
        const editor = editorRef.current;
        const moved = image.closest('[data-attachment]') || image;
        const target = e.target.closest?.('[data-attachment]');
        const range = imageDropRange(e.clientX, e.clientY);
        if (target === moved || moved.contains(range.commonAncestorContainer)) {
          draggedImageRef.current = null;
          return;
        }
        if (
          (moved.matches('[data-attachment]') &&
            moved.style.display !== 'inline-block') ||
          target
        ) {
          // Keep attachment blocks outside paragraphs and other attachment blocks.
          let block =
            target ||
            (range.startContainer.nodeType === 1
              ? range.startContainer
              : range.startContainer.parentElement);
          while (block && block.parentElement !== editor && block !== editor)
            block = block.parentElement;
          if (block && block !== editor) {
            const rect = block.getBoundingClientRect();
            block.insertAdjacentElement(
              e.clientY < rect.top + rect.height / 2
                ? 'beforebegin'
                : 'afterend',
              moved,
            );
          } else editor.append(moved);
        } else range.insertNode(moved);
        const caret = document.createRange();
        caret.setStartAfter(moved);
        caret.collapse(true);
        savedRange.current = caret.cloneRange();
        draggedImageRef.current = null;
        refreshAttachmentDecorations();
        emitNow();
        setSelectedImage(image);
        return;
      }

      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;
      e.preventDefault();
      for (const f of files) {
        const n = f.name.toLowerCase();
        const isDoc = /\.(docx|html?|md|markdown|txt)$/i.test(n);
        // eslint-disable-next-line no-await-in-loop
        if (isDoc) await importDocumentFile(f);
        // eslint-disable-next-line no-await-in-loop
        else await handleAttachFile(f);
      }
    };
    const headingValue =
      currentBlock() === 'h1'
        ? 'h1'
        : currentBlock() === 'h2'
          ? 'h2'
          : currentBlock() === 'h3'
            ? 'h3'
            : currentBlock() === 'blockquote'
              ? 'blockquote'
              : currentBlock() === 'pre'
                ? 'pre'
                : 'p';
    const wrapperClass = cn(
      'rich-text-editor',
      fullscreen && 'rte-fullscreen-editor',
      className,
    );
    const shell = (children) =>
      fullscreen
        ? createPortal(
            <div className='rte-fullscreen'>{children}</div>,
            document.body,
          )
        : children;
    const insertItems = [
      {
        key: 'image',
        icon: <ImageIcon className='rte-icon' />,
        label: 'Image',
        hint: 'Upload and insert inline',
        onSelect: () => imageInputRef.current?.click(),
      },
      {
        key: 'table',
        icon: <TableIcon className='rte-icon' />,
        label: 'Table',
        hint: 'Insert rows and columns',
        onSelect: () => {
          saveSelection();
          setTblOpen(true);
        },
      },
      ...(allowAttachments
        ? [
            {
              key: 'attachment',
              icon: <Paperclip className='rte-icon' />,
              label: 'File attachment',
              hint: 'Upload a file',
              onSelect: openAttach,
            },
          ]
        : []),
    ];
    return (
      <>
        {shell(
          <div className={wrapperClass}>
            {/* ── Toolbar ── */}
            <div
              className='rte-toolbar'
              inert={disabled ? true : undefined}
              aria-disabled={disabled}
              onMouseDown={(e) => {
                if (!e.target.closest('input,select')) e.preventDefault();
              }}
            >
              <DropdownMenu
                onOpenChange={(open) => {
                  if (open) saveSelection();
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    disabled={disabled || processing}
                    className='rte-insert-button'
                    onMouseDown={() => saveSelection()}
                  >
                    {processing ? (
                      <Loader2 className='rte-spinner' />
                    ) : (
                      <Plus className='rte-icon-small' />
                    )}
                    Insert
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align='start' className='rte-insert-menu'>
                  {insertItems.map((item) => (
                    <DropdownMenuItem
                      key={item.key}
                      onSelect={(event) => {
                        item.onSelect();
                      }}
                      className='rte-insert-item'
                    >
                      <span className='rte-muted'>{item.icon}</span>

                      <span className='rte-column'>
                        <span className='rte-text-small'>{item.label}</span>
                        <span className='rte-hint'>{item.hint}</span>
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Divider />

              {notificationOptions.length > 0 && (
                <>
                  <DropdownMenu
                    onOpenChange={(open) => {
                      if (open) saveSelection();
                    }}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='outline'
                        size='sm'
                        className='rte-insert-button'
                        disabled={disabled || processing}
                        onMouseDown={() => saveSelection()}
                      >
                        Insert Option <ChevronDown className='rte-icon-small' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align='start'
                      className='rte-notification-menu'
                    >
                      {notificationOptions.map((option) => (
                        <DropdownMenuItem
                          key={option.value}
                          onSelect={() =>
                            insertNotificationOption(option.value)
                          }
                        >
                          {option.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Divider />
                </>
              )}

              {/* Block / heading */}
              <div
                onMouseDown={() => {
                  saveSelection();
                }}
              >
                <ReusableSelect
                  value={headingValue}
                  onChange={(v) => setBlock(v)}
                  options={[
                    {
                      value: 'p',
                      label: 'Paragraph',
                    },
                    {
                      value: 'h1',
                      label: 'Heading 1',
                    },
                    {
                      value: 'h2',
                      label: 'Heading 2',
                    },
                    {
                      value: 'h3',
                      label: 'Heading 3',
                    },
                    {
                      value: 'blockquote',
                      label: 'Quote',
                    },
                    {
                      value: 'pre',
                      label: 'Code block',
                    },
                  ]}
                  optionConfig={{
                    getValue: (o) => o.value,
                    getLabel: (o) => o.label,
                  }}
                  triggerClassName='rte-font-select'
                />
              </div>

              {/* Font family */}
              <div
                onMouseDown={() => {
                  saveSelection();
                }}
              >
                <ReusableSelect
                  value={selectedFontFamily}
                  options={FONT_FAMILIES.map((font) => ({
                    value: font.value,
                    label: font.label,
                  }))}
                  onChange={(value) => {
                    setSelectedFontFamily(value);
                    if (value) {
                      exec('fontName', value);
                    } else {
                      exec('removeFormat');
                    }
                  }}
                  placeholder='Font'
                  triggerClassName='rte-font-select'
                  optionConfig={{
                    getValue: (option) => option.value,
                    getLabel: (option) => option.label,
                    renderOption: (option) => (
                      <span
                        style={{
                          fontFamily: option.value || undefined,
                        }}
                      >
                        {option.label}
                      </span>
                    ),
                  }}
                />
              </div>

              {/* Font size */}
              <div
                onMouseDown={() => {
                  saveSelection();
                }}
              >
                <ReusableSelect
                  value={selectedFontSize}
                  options={FONT_SIZES.map((s) => ({
                    value: s.value,
                    label: `${s.label}px`,
                  }))}
                  onChange={(v) => {
                    setSelectedFontSize(v);
                    exec('fontSize', v);
                  }}
                  placeholder='Size'
                  triggerClassName='rte-size-select'
                  optionConfig={{
                    getValue: (o) => o.value,
                    getLabel: (o) => o.label,
                  }}
                />
              </div>

              <Divider />

              <ToolBtn
                title='Bold (Ctrl+B)'
                active={isActive('bold')}
                onClick={() => exec('bold')}
              >
                <Bold className='rte-icon' />
              </ToolBtn>
              <ToolBtn
                title='Italic (Ctrl+I)'
                active={isActive('italic')}
                onClick={() => exec('italic')}
              >
                <Italic className='rte-icon' />
              </ToolBtn>
              <ToolBtn
                title='Underline (Ctrl+U)'
                active={isActive('underline')}
                onClick={() => exec('underline')}
              >
                <Underline className='rte-icon' />
              </ToolBtn>
              <ToolBtn
                title='Strikethrough'
                active={isActive('strikeThrough')}
                onClick={() => exec('strikeThrough')}
              >
                <Strikethrough className='rte-icon' />
              </ToolBtn>
              <ToolBtn
                title='Inline code'
                active={(() => {
                  const node = window.getSelection()?.anchorNode;
                  let cur = node || null;
                  while (cur && cur !== editorRef.current) {
                    if (cur.nodeType === 1 && cur.tagName === 'CODE')
                      return true;
                    cur = cur.parentNode;
                  }
                  return false;
                })()}
                onClick={() => {
                  restoreSelection();
                  editorRef.current?.focus();
                  const sel = window.getSelection();
                  const text = sel?.toString() || '';
                  if (text) {
                    document.execCommand(
                      'insertHTML',
                      false,
                      `<code>${escapeHtml(text)}</code>\u200B`,
                    );
                  } else {
                    // Insert empty <code> with ZWSP inside, then place caret INSIDE.
                    const codeEl = document.createElement('code');
                    codeEl.textContent = '\u200B';
                    const range = sel?.getRangeAt(0);
                    if (range) {
                      range.deleteContents();
                      range.insertNode(codeEl);
                      const inside = document.createRange();
                      inside.setStart(codeEl.firstChild, 1);
                      inside.collapse(true);
                      sel.removeAllRanges();
                      sel.addRange(inside);
                    }
                  }
                  emit();
                  refresh();
                }}
              >
                <CodeIcon className='rte-icon' />
              </ToolBtn>

              <Divider />

              {/* Text color */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type='button'
                    title='Text color'
                    onMouseDown={(e) => {
                      e.preventDefault();
                      saveSelection();
                    }}
                    className='rte-palette-trigger'
                  >
                    <Palette className='rte-icon' />
                  </button>
                </PopoverTrigger>
                <PopoverContent className='rte-color-popup'>
                  <ColorGrid
                    onPick={(c) => exec('foreColor', c)}
                    onReset={() => resetEditorColor('text')}
                  />{' '}
                </PopoverContent>
              </Popover>

              {/* Highlight */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type='button'
                    title='Highlight'
                    onMouseDown={(e) => {
                      e.preventDefault();
                      saveSelection();
                    }}
                    className='rte-palette-trigger'
                  >
                    <Highlighter className='rte-icon' />
                  </button>
                </PopoverTrigger>
                <PopoverContent className='rte-color-popup'>
                  <ColorGrid
                    onPick={(c) => exec('hiliteColor', c)}
                    onReset={() => resetEditorColor('highlight')}
                  />
                </PopoverContent>
              </Popover>

              <Divider />

              <ToolBtn title='Align left' onClick={() => exec('justifyLeft')}>
                <AlignLeft className='rte-icon' />
              </ToolBtn>
              <ToolBtn
                title='Align center'
                onClick={() => exec('justifyCenter')}
              >
                <AlignCenter className='rte-icon' />
              </ToolBtn>
              <ToolBtn title='Align right' onClick={() => exec('justifyRight')}>
                <AlignRight className='rte-icon' />
              </ToolBtn>
              <ToolBtn title='Justify' onClick={() => exec('justifyFull')}>
                <AlignJustify className='rte-icon' />
              </ToolBtn>

              <Divider />

              <ToolBtn
                title='Superscript'
                active={isActive('superscript')}
                onClick={() => exec('superscript')}
              >
                <Superscript className='rte-icon' />
              </ToolBtn>
              <ToolBtn
                title='Subscript'
                active={isActive('subscript')}
                onClick={() => exec('subscript')}
              >
                <Subscript className='rte-icon' />
              </ToolBtn>

              <Divider />

              <ToolBtn
                title='Bulleted list'
                onClick={() => exec('insertUnorderedList')}
              >
                <List className='rte-icon' />
              </ToolBtn>
              <ToolBtn
                title='Numbered list'
                onClick={() => exec('insertOrderedList')}
              >
                <ListOrdered className='rte-icon' />
              </ToolBtn>
              <ToolBtn title='Checklist' onClick={insertChecklist}>
                <ListChecks className='rte-icon' />
              </ToolBtn>
              <ToolBtn
                title='Decrease indent'
                onClick={() => changeIndent('decrease')}
              >
                <IndentDecrease className='rte-icon' />
              </ToolBtn>

              <ToolBtn
                title='Increase indent'
                onClick={() => changeIndent('increase')}
              >
                <IndentIncrease className='rte-icon' />
              </ToolBtn>
              {/* <ToolBtn title="Increase indent" onClick={() => exec('indent')}>
          <IndentIncrease className="rte-icon" />
        </ToolBtn> */}

              <Divider />

              <ToolBtn title='Insert link (Ctrl+K)' onClick={openLink}>
                <LinkIcon className='rte-icon' />
              </ToolBtn>
              <ToolBtn title='Remove link' onClick={removeLink}>
                <Unlink className='rte-icon' />
              </ToolBtn>
              {/* <ToolBtn title="Insert image" onClick={openImage}>
              <ImageIcon className="rte-icon" />
            </ToolBtn> */}
              {/* <ToolBtn title="Insert table" onClick={() => {
                saveSelection();
                setTblOpen(true);
            }}>
                <TableIcon className="rte-icon"/>
              </ToolBtn> */}
              <ToolBtn
                title='Horizontal rule'
                onClick={() => exec('insertHorizontalRule')}
              >
                <Minus className='rte-icon' />
              </ToolBtn>

              {/* Special chars / emoji */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type='button'
                    title='Insert special character / emoji'
                    onMouseDown={(e) => {
                      e.preventDefault();
                      saveSelection();
                    }}
                    className='rte-palette-trigger'
                  >
                    <Smile className='rte-icon' />
                  </button>
                </PopoverTrigger>
                <PopoverContent className='rte-symbol-popup'>
                  <SpecialCharGrid onPick={(ch) => exec('insertText', ch)} />
                </PopoverContent>
              </Popover>

              {/* Table edit menu — appears when cursor inside table */}
              {inTable && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type='button'
                      title='Table options'
                      onMouseDown={(e) => {
                        e.preventDefault();
                        saveSelection();
                      }}
                      className='rte-table-trigger'
                    >
                      <TableIcon className='rte-icon' />
                      <ChevronDown className='rte-icon-tiny' />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className='rte-table-popup'>
                    <TableMenu
                      onAction={(action) => {
                        restoreSelection();
                        runTableAction(action);
                        emit();
                      }}
                    />
                  </PopoverContent>
                </Popover>
              )}

              <Divider />

              {/* <ToolBtn title="Find & replace (Ctrl+F)" onClick={() => setFindOpen(true)}>
                <Search className="rte-icon"/>
              </ToolBtn> */}
              {/* <ToolBtn title="Print" onClick={printContent}>
                <Printer className="rte-icon"/>
              </ToolBtn> */}
              <ToolBtn title='Undo (Ctrl+Z)' onClick={() => exec('undo')}>
                <Undo2 className='rte-icon' />
              </ToolBtn>
              <ToolBtn title='Redo (Ctrl+Y)' onClick={() => exec('redo')}>
                <Redo2 className='rte-icon' />
              </ToolBtn>
              {/* <ToolBtn title="Clear formatting" onClick={() => exec('removeFormat')}>
                <Eraser className="rte-icon"/>
              </ToolBtn> */}

              <div className='rte-toolbar-end'>
                {/* <ToolBtn
              title={
                attaching
                  ? 'Attaching…'
                  : 'Attach file (PDF, image, video, audio, any file)'
              }
              onClick={openAttach}
              disabled={attaching}
            >
              <Paperclip className="rte-icon" />
            </ToolBtn> */}
                <input
                  ref={attachInputRef}
                  type='file'
                  className='rte-hidden'
                  onChange={onAttachChange}
                />
                <ToolBtn
                  title='Import document (.docx, .html, .md, .txt)'
                  onClick={openImport}
                >
                  <FileUp className='rte-icon' />
                </ToolBtn>
                <input
                  ref={importInputRef}
                  type='file'
                  accept='.docx,.html,.htm,.md,.markdown,.txt,image/*'
                  className='rte-hidden'
                  onChange={onImportChange}
                />
                <ToolBtn
                  title='Source code (paste raw HTML)'
                  onClick={openSource}
                >
                  <Code2 className='rte-icon' />
                </ToolBtn>
                <ToolBtn
                  title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  onClick={() => {
                    setSelectedImage(null);
                    fullscreenHtmlRef.current = emitNow();
                    setFullscreen((f) => !f);
                  }}
                >
                  {fullscreen ? (
                    <Minimize2 className='rte-icon' />
                  ) : (
                    <Maximize2 className='rte-icon' />
                  )}
                </ToolBtn>
              </div>
            </div>

            {/* ── Editable area ── */}
            <div
              ref={editorRef}
              contentEditable={!disabled}
              suppressContentEditableWarning
              spellCheck
              dir='auto'
              data-placeholder={placeholder}
              onBeforeInput={(e) => {
                const editor = editorRef.current;
                if (!editor) return;
                const inputEvent = e.nativeEvent;
                // Deleting must always be allowed.
                if (inputEvent.inputType?.startsWith('delete')) {
                  return;
                }
                const currentLength = getTextLength(editor);
                const selection = window.getSelection();
                const selectedLength =
                  selection && editor.contains(selection.anchorNode)
                    ? selection.toString().length
                    : 0;
                const insertedLength = inputEvent.data?.length ?? 0;
                const nextLength =
                  currentLength - selectedLength + insertedLength;
                if (nextLength > maxCharacters) {
                  e.preventDefault();
                }
              }}
              onInput={scheduleEmit}
              onMouseDown={(e) => {
                const target = e.target;
                const attachment = target.closest('[data-attachment]');
                const removeBtn = target.closest(`[${ATTACHMENT_REMOVE_ATTR}]`);
                if (attachment && !removeBtn && !target.closest('img')) {
                  e.preventDefault();
                }
              }}
              onClick={(e) => {
                if (disabled) return;
                const target = e.target;
                const clickedImage = target.closest('img');
                if (clickedImage && editorRef.current.contains(clickedImage)) {
                  setSelectedImage(clickedImage);
                  return;
                }
                setSelectedImage(null);
                const removeBtn = target.closest(`[${ATTACHMENT_REMOVE_ATTR}]`);
                if (removeBtn) {
                  e.preventDefault();
                  removeBtn.closest('[data-attachment]')?.remove();
                  emit();
                  return;
                }
                const checkUi = target.closest('[data-check-ui]');
                if (checkUi) {
                  e.preventDefault();
                  const isChecked = checkUi.dataset.checked === 'true';
                  const nextChecked = !isChecked;
                  checkUi.dataset.checked = String(nextChecked);
                  checkUi.setAttribute('aria-checked', String(nextChecked));
                  if (nextChecked) {
                    checkUi.innerHTML = '✓';
                    checkUi.style.backgroundColor = 'hsl(var(--primary))';
                    checkUi.style.borderColor = 'hsl(var(--primary))';
                  } else {
                    checkUi.innerHTML = '';
                    checkUi.style.backgroundColor = 'transparent';
                    checkUi.style.borderColor = '#9ca3af';
                  }
                  emit();
                  return;
                }
                const anchor = target.closest('a');
                if (anchor && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  const href = anchor.getAttribute('href');
                  if (href && isSafeUrl(href)) {
                    window.open(href, '_blank', 'noopener,noreferrer');
                  }
                }
              }}
              onBlur={() => {
                saveSelection();
                emitNow();
              }}
              onKeyDown={(e) => {
                // Keyboard shortcuts (TinyMCE parity)
                const meta = e.ctrlKey || e.metaKey;
                if (meta && ['z', 'y'].includes(e.key.toLowerCase())) {
                  e.preventDefault();
                  exec(
                    e.key.toLowerCase() === 'y' || e.shiftKey ? 'redo' : 'undo',
                  );
                  return;
                }
                if (
                  [' ', 'Enter'].includes(e.key) &&
                  e.target.closest('[data-check-ui]')
                ) {
                  e.preventDefault();
                  e.target.click();
                  return;
                }
                if (meta && e.key.toLowerCase() === 'k') {
                  e.preventDefault();
                  openLink();
                } else if (meta && e.key.toLowerCase() === 'f') {
                  e.preventDefault();
                  setFindOpen(true);
                } else if (meta && e.shiftKey && e.key.toLowerCase() === 'x') {
                  e.preventDefault();
                  exec('strikeThrough');
                } else if (e.key === ' ' || e.key === 'Enter') {
                  // Auto-link: detect a URL just before the caret and wrap it.
                  setTimeout(() => autoLinkBeforeCaret(), 0);
                }
              }}
              onKeyUp={() => {
                saveSelection();
                setInTable(!!closestTableCell());
                refresh();
              }}
              onMouseUp={() => {
                saveSelection();
                setInTable(!!closestTableCell());
                refresh();
              }}
              onPaste={(e) => {
                if (disabled) {
                  e.preventDefault();
                  return;
                }
                return onPaste(e);
              }}
              onDrop={(e) => {
                if (disabled) {
                  e.preventDefault();
                  return;
                }
                return onEditorDrop(e);
              }}
              onDragStart={onImageDragStart}
              onDragEnd={() => {
                draggedImageRef.current = null;
                setImageDropCaret(null);
              }}
              onDragOver={(e) => {
                if (
                  !disabled &&
                  (draggedImageRef.current ||
                    e.dataTransfer?.types?.includes('Files'))
                ) {
                  e.preventDefault();
                  if (draggedImageRef.current) {
                    e.dataTransfer.dropEffect = 'move';
                    const range = imageDropRange(e.clientX, e.clientY);
                    const rect = range.getBoundingClientRect();
                    setImageDropCaret(
                      rect.height
                        ? {
                            left: rect.left,
                            top: rect.top,
                            height: rect.height,
                          }
                        : null,
                    );
                  }
                }
              }}
              className='rte-content'
              style={
                fullscreen
                  ? { minHeight: 0, maxHeight: 'none', height: '100%' }
                  : { minHeight, maxHeight: maxHeight ?? 400 }
              }
            />

            {/* ── Footer ── */}
            <div className='rte-statusbar'>
              <span>
                click <span className='rte-key'>{'</>'}</span> to edit raw HTML
              </span>
              <span className='rte-mono'>
                {(() => {
                  const text = (value || '')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/&nbsp;/g, ' ')
                    .trim();
                  const chars = getHtmlTextLength(value || '');
                  const words = text
                    ? text.split(/\s+/).filter(Boolean).length
                    : 0;
                  return `${words} word${words === 1 ? '' : 's'} · ${chars.toLocaleString()}${Number.isFinite(maxCharacters) ? ` / ${maxCharacters.toLocaleString()}` : ''} chars`;
                })()}
              </span>
            </div>
          </div>,
        )}
        {selectedImage &&
          imageRect &&
          !disabled &&
          createPortal(
            <div
              className='rte-image-resizer'
              style={{
                position: 'fixed',
                left: imageRect.left,
                top: imageRect.top,
                width: imageRect.width,
                height: imageRect.height,
                zIndex: 205,
                pointerEvents: 'none',
                border: '2px solid #1b224c',
              }}
            >
              <div
                style={{
                  position: 'fixed',
                  left: Math.max(
                    8,
                    Math.min(imageRect.left, window.innerWidth - 740),
                  ),
                  top: Math.max(8, imageRect.top - 48),
                  maxWidth: 'calc(100vw - 16px)',
                  flexWrap: 'wrap',
                  display: 'flex',
                  gap: 6,
                  padding: 6,
                  background: 'white',
                  border: '1px solid #7c3aed',
                  borderRadius: 6,
                  pointerEvents: 'auto',
                  color: '#172033',
                }}
              >
                <select
                  aria-label='Image position'
                  value={imagePosition}
                  onChange={(e) => positionSelectedImage(e.target.value)}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    padding: 4,
                    fontSize: 12,
                  }}
                >
                  <option value='inline'>Inline — between words</option>
                  <option value='left'>Left</option>
                  <option value='center'>Center</option>
                  <option value='right'>Right</option>
                  <option value='wrapLeft'>Wrap text — image left</option>
                  <option value='wrapRight'>Wrap text — image right</option>
                </select>
                <span
                  style={{
                    fontSize: 11,
                    alignSelf: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Drag image to move
                </span>
                <label style={{ fontSize: 12 }}>
                  Width{' '}
                  <input
                    aria-label='Image width'
                    type='number'
                    min='24'
                    max='4096'
                    value={imageWidth}
                    onChange={(e) => setImageWidth(e.target.value)}
                    style={{ width: 72, border: '1px solid #ddd', padding: 3 }}
                  />
                </label>
                <button
                  type='button'
                  onClick={() => resizeSelectedImage(Number(imageWidth))}
                >
                  Apply
                </button>
                <button
                  type='button'
                  onClick={() => {
                    selectedImage.style.width = '';
                    selectedImage.style.height = 'auto';
                    selectedImage.removeAttribute('width');
                    selectedImage.removeAttribute('height');
                    emitNow();
                    updateImageRect();
                  }}
                >
                  Reset
                </button>
                <button
                  type='button'
                  aria-label='Close image resize'
                  onClick={() => setSelectedImage(null)}
                >
                  ×
                </button>
              </div>
              <button
                type='button'
                aria-label='Drag to resize image'
                onPointerDown={startImageResize}
                style={{
                  position: 'absolute',
                  right: -7,
                  bottom: -7,
                  width: 14,
                  height: 14,
                  background: '#7c3aed',
                  border: '2px solid white',
                  cursor: 'nwse-resize',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                }}
              />
            </div>,
            document.body,
          )}
        {imageDropCaret &&
          createPortal(
            <div
              aria-hidden='true'
              style={{
                position: 'fixed',
                left: imageDropCaret.left,
                top: imageDropCaret.top,
                height: imageDropCaret.height,
                width: 2,
                background: '#7c3aed',
                pointerEvents: 'none',
                zIndex: 206,
              }}
            />,
            document.body,
          )}
        {/* ── Link dialog ── */}
        <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
          <DialogContent className='rte-dialog-link'>
            <DialogHeader>
              <DialogTitle>Insert link</DialogTitle>
            </DialogHeader>
            <div className='rte-fields'>
              <div>
                <Label className='rte-text-xs'>URL</Label>
                <Input
                  value={linkUrl}
                  onChange={(e) => {
                    setLinkUrl(e.target.value);
                    setLinkError('');
                  }}
                  placeholder='https://example.com or www.example.com'
                  autoFocus
                />
              </div>
              <div>
                <Label className='rte-text-xs'>Text to display</Label>
                <Input
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder='(uses selection / URL if empty)'
                />
                {linkError && (
                  <p role='alert' className='rte-error-small'>
                    {linkError}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant='ghost' onClick={() => setLinkOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={applyLink}
                disabled={!linkUrl.trim() || disabled}
              >
                Insert
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* ── Image dialog ── */}
        <Dialog
          open={messageDialog.open}
          onOpenChange={(open) =>
            setMessageDialog((prev) => ({
              ...prev,
              open,
            }))
          }
        >
          <DialogContent className='rte-dialog-message'>
            <div className='rte-message-body'>
              <DialogHeader className='rte-message-header'>
                <DialogTitle className='rte-message-title'>
                  {messageDialog.title}
                </DialogTitle>

                <p className='rte-message-text'>{messageDialog.message}</p>
              </DialogHeader>
            </div>

            <DialogFooter className='rte-message-footer'>
              <Button
                type='button'
                className='rte-confirm-button'
                onClick={() =>
                  setMessageDialog((prev) => ({
                    ...prev,
                    open: false,
                  }))
                }
              >
                Got it
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* ── Table dialog ── */}
        <Dialog open={tblOpen} onOpenChange={setTblOpen}>
          <DialogContent className='rte-dialog-table'>
            <DialogHeader>
              <DialogTitle>Insert table</DialogTitle>
            </DialogHeader>
            <div className='rte-table-fields'>
              <div>
                <Label className='rte-text-xs'>Rows</Label>
                <Input
                  type='number'
                  min={1}
                  max={20}
                  value={tblRows}
                  placeholder='e.g. 3'
                  onChange={(e) =>
                    setTblRows(e.target.value.replace(/[^0-9]/g, ''))
                  }
                />
              </div>
              <div>
                <Label className='rte-text-xs'>Columns</Label>
                <Input
                  type='number'
                  min={1}
                  max={10}
                  value={tblCols}
                  placeholder='e.g. 3'
                  onChange={(e) =>
                    setTblCols(e.target.value.replace(/[^0-9]/g, ''))
                  }
                />
              </div>
            </div>
            <label className='rte-header-option'>
              <input
                type='checkbox'
                checked={tblHeader}
                onChange={(e) => setTblHeader(e.target.checked)}
                className='rte-checkbox'
              />
              Include header row
            </label>
            <DialogFooter>
              <Button variant='ghost' onClick={() => setTblOpen(false)}>
                Cancel
              </Button>
              <Button onClick={insertTable} disabled={!tblRows || !tblCols}>
                Insert
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* ── Source code dialog ── */}
        <Dialog open={sourceOpen} onOpenChange={setSourceOpen}>
          <DialogContent className='rte-dialog-source'>
            <DialogHeader>
              <DialogTitle className='rte-row'>
                <Code2 className='rte-icon' /> Source code
              </DialogTitle>
            </DialogHeader>
            {insertError && (
              <p role='alert' className='rte-error'>
                {insertError}
              </p>
            )}
            <textarea
              value={sourceDraft}
              onChange={(e) => setSourceDraft(e.target.value)}
              spellCheck={false}
              className='rte-source-input'
              placeholder='<!DOCTYPE html>&#10;<html>...</html>'
            />
            <p className='rte-hint'>
              Paste HTML, tables, and inline styles. Unsafe scripts and embeds
              are removed.
            </p>
            <DialogFooter>
              <Button variant='ghost' onClick={() => setSourceOpen(false)}>
                Cancel
              </Button>
              <Button onClick={applySource} disabled={disabled}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* ── Find & replace floating panel (non-blocking) ── */}
        {findOpen && (
          <div
            className={cn(
              'rte-find-panel',
              fullscreen ? 'rte-find-fullscreen' : 'rte-find-normal',
            )}
            role='dialog'
            aria-label='Find and replace'
          >
            <div className='rte-between'>
              <div className='rte-find-title'>
                <Search className='rte-icon' /> Find &amp; replace
              </div>
              <button
                type='button'
                onClick={() => setFindOpen(false)}
                className='rte-find-close'
                aria-label='Close'
              >
                <X className='rte-icon' />
              </button>
            </div>
            <Input
              value={findQuery}
              onChange={(e) => setFindQuery(e.target.value)}
              placeholder='Find'
              autoFocus
              className='rte-compact-input'
            />
            <Input
              value={replaceWith}
              onChange={(e) => setReplaceWith(e.target.value)}
              placeholder='Replace with'
              className='rte-compact-input'
            />
            <div className='rte-find-options'>
              <label className='rte-match-case'>
                <input
                  type='checkbox'
                  checked={matchCase}
                  onChange={(e) => setMatchCase(e.target.checked)}
                  className='rte-checkbox'
                />
                Match case
              </label>
              <span className='rte-hint'>{findStatus}</span>
            </div>
            <div className='rte-find-actions'>
              <Button
                size='sm'
                variant='outline'
                className='rte-find-button'
                onClick={findNext}
                disabled={!findQuery}
              >
                Find next
              </Button>
              <Button
                size='sm'
                variant='outline'
                className='rte-find-button'
                onClick={replaceCurrent}
                disabled={!findQuery}
              >
                Replace
              </Button>
              <Button
                size='sm'
                className='rte-find-button'
                onClick={replaceAllMatches}
                disabled={!findQuery}
              >
                Replace all
              </Button>
            </div>
          </div>
        )}
        <input
          ref={imageInputRef}
          type='file'
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          className='rte-hidden'
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) {
              void handleUploadedImage(file);
            }
          }}
        />

        {insertError && <div className='rte-error-banner'>{insertError}</div>}
      </>
    );
  },
);
RichTextEditor.displayName = 'RichTextEditor';
/* ── Color grid ── */
const ColorGrid = ({ onPick, onReset }) => {
  const [custom, setCustom] = useState('#000000');
  return (
    <div className='rte-stack'>
      <div className='rte-color-grid'>
        {PRESET_COLORS.map((color) => (
          <button
            key={color}
            type='button'
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(color)}
            title={color}
            className='rte-color-swatch'
            style={{ backgroundColor: color }}
          />
        ))}
      </div>

      <div className='rte-row'>
        <input
          type='color'
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          className='rte-color-input'
        />

        <Input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          className='rte-color-value'
        />

        <Button
          type='button'
          size='sm'
          className='rte-control'
          onClick={() => onPick(custom)}
        >
          Apply
        </Button>
      </div>

      <Button
        type='button'
        size='sm'
        variant='outline'
        className='rte-color-reset'
        onMouseDown={(e) => e.preventDefault()}
        onClick={onReset}
      >
        Reset color
      </Button>
    </div>
  );
};
/* ── Special char / emoji grid ── */
const SPECIAL_CHARS = [
  '©',
  '®',
  '™',
  '§',
  '¶',
  '†',
  '‡',
  '•',
  '…',
  '‰',
  '′',
  '″',
  '€',
  '£',
  '¥',
  '¢',
  '$',
  '₹',
  '₽',
  '¤',
  '±',
  '×',
  '÷',
  '≠',
  '≈',
  '≤',
  '≥',
  '∞',
  '∑',
  '√',
  'π',
  'µ',
  '°',
  '∆',
  '∂',
  '∫',
  '←',
  '→',
  '↑',
  '↓',
  '↔',
  '⇒',
  '⇐',
  '⇔',
  '★',
  '☆',
  '♥',
  '♦',
  '♣',
  '♠',
  '✓',
  '✗',
  '✔',
  '✘',
  '☐',
  '☑',
  '☒',
  '😀',
  '😃',
  '😄',
  '😁',
  '😊',
  '😍',
  '🤔',
  '😎',
  '🙌',
  '👍',
  '👎',
  '🎉',
  '🚀',
  '💡',
  '🔥',
  '✅',
  '❌',
  '⭐',
  '📌',
  '📎',
  '📧',
  '📞',
  '🗓️',
  '⏰',
  '💼',
  '📊',
];
const SpecialCharGrid = ({ onPick }) => (
  <div className='rte-symbol-grid'>
    {SPECIAL_CHARS.map((ch) => (
      <button
        key={ch}
        type='button'
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onPick(ch)}
        className='rte-symbol'
      >
        {ch}
      </button>
    ))}
  </div>
);
/* ── Table edit menu ── */
const TableMenu = ({ onAction }) => {
  const Item = ({ label, action, danger }) => (
    <button
      type='button'
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onAction(action)}
      className={cn('rte-table-action', danger && 'rte-table-danger')}
    >
      {label}
    </button>
  );
  return (
    <div className='rte-column'>
      <Item label='Insert row above' action='addRowAbove' />
      <Item label='Insert row below' action='addRowBelow' />
      <Item label='Delete row' action='deleteRow' danger />
      <div className='rte-menu-divider' />
      <Item label='Insert column left' action='addColLeft' />
      <Item label='Insert column right' action='addColRight' />
      <Item label='Delete column' action='deleteCol' danger />
      <div className='rte-menu-divider' />
      <Item label='Toggle header row' action='toggleHeaderRow' />
      <Item label='Delete table' action='deleteTable' danger />
    </div>
  );
};
const STYLE_PRESERVE_RE =
  /<style\b[^>]*data-cert-style="1"[^>]*>[\s\S]*?<\/style>/gi;
const sanitizeHtml = (html) => {
  // Preserve raw <style data-cert-style="1"> blocks verbatim — DOMPurify
  // would otherwise strip/alter their CSS content (e.g. @import, complex
  // selectors). We swap them out for text placeholders, sanitize, then restore.
  // DOMPurify removes HTML comments, so comment placeholders would disappear.
  const styleBlocks = [];
  const swapped = html.replace(STYLE_PRESERVE_RE, (match) => {
    const idx = styleBlocks.push(match) - 1;
    return `__CERT_STYLE_PLACEHOLDER_${idx}__`;
  });
  const cleaned = DOMPurify.sanitize(swapped, {
    // KaTeX uses normal HTML, MathML and sometimes SVG.
    USE_PROFILES: {
      html: true,
      mathMl: true,
      svg: true,
      svgFilters: false,
    },
    ADD_TAGS: ['style', 'figure', 'figcaption', 'svg', 'path'],
    ADD_ATTR: [
      'target',
      'rel',
      'style',
      'class',
      'aria-hidden',
      'role',
      'xmlns',
      'encoding',
      'contenteditable',
      'data-latex',
      'data-display-mode',
      'data-checklist',
      'data-cert-style',
      'data-cert-qr',
      'data-cert-qr-host',
      'download',
      'controls',
      'data-attachment',
      'data-name',
      'data-rte-attachment-remove',
      'contenteditable',
      'xmlns',
      'viewBox',
      'fill',
      'stroke',
      'stroke-width',
      'stroke-linecap',
      'stroke-linejoin',
      'title',
      'class',
      'contenteditable',
      'data-latex',
      'data-display-mode',
      'data-rendered-latex',
      'data-check-ui',
      'data-checklist-item',
      'aria-hidden',
      'data-asset-id',
    ],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
    FORBID_ATTR: [
      'onerror',
      'onload',
      'onclick',
      'onmouseover',
      'onfocus',
      'onmouseenter',
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|data:|\/(?!\/)|#)/i,
  });
  return cleaned.replace(
    /__CERT_STYLE_PLACEHOLDER_(\d+)__/g,
    (_m, i) => styleBlocks[Number(i)] ?? '',
  );
};
/** Strip Microsoft Word / Google Docs paste cruft before insertion. */
const cleanWordHtml = (html) => {
  if (!html) return '';
  let s = html;
  // Conditional comments + XML declarations
  s = s.replace(/<!--\[if[\s\S]*?endif\]-->/gi, '');
  s = s.replace(/<\?xml[\s\S]*?\?>/gi, '');
  // Word/Office namespaced tags <o:p>, <w:*>, <m:*>, <v:*>, <st1:*>
  s = s.replace(/<\/?(?:o|w|m|v|st\d):[^>]*>/gi, '');
  // mso-* style declarations — handle both " and ' quoted attributes
  s = s.replace(/style\s*=\s*(["'])([\s\S]*?)\1/gi, (_m, q, css) => {
    const cleaned = css
      .split(';')
      .map((d) => d.trim())
      .filter((d) => d && !/^mso-/i.test(d))
      .join('; ');
    return cleaned ? `style=${q}${cleaned}${q}` : '';
  });
  // class="Mso..." (single or double quoted)
  s = s.replace(/\s*class\s*=\s*(["'])Mso[^"']*\1/gi, '');
  // lang / xml:lang attrs (single or double quoted)
  s = s.replace(/\s*(?:xml:)?lang\s*=\s*(["'])[^"']*\1/gi, '');
  // Empty spans (no inline style)
  s = s.replace(/<span>\s*<\/span>/gi, '');
  return s;
};
/* ── Helpers ── */
function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
function escapeAttr(s) {
  return escapeHtml(s);
}
export default RichTextEditor;
